import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  LinearProgress,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  DragIndicator as DragIndicatorIcon,
  ArrowBackIosNew as ArrowBackIcon,
  ArrowForwardIos as ArrowForwardIcon,
  MyLocation as MyLocationIcon,
  Place as PlaceIcon
} from '@mui/icons-material';
import { keyframes } from '@mui/system';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { getDistance } from 'geolib';
import { logEvent } from 'firebase/analytics';
import { useNavigate } from 'react-router-dom';

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.4); }
  50% { box-shadow: 0 0 20px 4px rgba(76, 175, 80, 0.2); }
`;

// Time slots - generate all possible hours (6am to midnight)
const generateTimeSlots = (startHour, endHour) => {
  const times = [];
  for (let h = startHour; h < endHour; h++) {
    times.push(`${String(h).padStart(2, '0')}:00`);
  }
  return times;
};

// Default schedule (9am - 11pm)
const DEFAULT_SCHEDULE = {
  weekday: { start: 9, end: 23 },
  saturday: { start: 9, end: 23 },
  sunday: { start: 9, end: 23 }
};

const ALL_TIMES = generateTimeSlots(6, 24); // All possible times for data lookup
const TIME_SLOT_WIDTH = 48;
const TIME_SLOT_GAP = 3;
const TIME_WINDOW_SIZE = 6;
const PERIODS = [
  { key: 'morning', labelKey: 'morning', start: 6, end: 12 },
  { key: 'afternoon', labelKey: 'afternoon', start: 12, end: 18 },
  { key: 'evening', labelKey: 'evening', start: 18, end: 24 }
];

// Compare current Toronto wall-clock time against an unzoned ET ISO string
// (e.g. "2026-05-07T10:00:00").
const getTorontoNowString = () => {
  if (typeof window !== 'undefined' && window.__predictiveNowOverride) {
    return window.__predictiveNowOverride;
  }
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Toronto',
    hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  }).formatToParts(new Date()).reduce((acc, p) => {
    if (p.type !== 'literal') acc[p.type] = p.value;
    return acc;
  }, {});
  let hour = parts.hour;
  if (hour === '24') hour = '00';
  return `${parts.year}-${parts.month}-${parts.day}T${hour}:${parts.minute}:${parts.second}`;
};

const isPredictiveSlotVisible = (slot) => {
  const showAt = slot?.predictiveDisplay?.showAt;
  if (!showAt) return true;
  return getTorontoNowString() >= showAt;
};

const normalizeCourtSearchValue = (value = '') =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');

// Get schedule for a court based on its name
const getCourtSchedule = (courtName, schedules) => {
  if (!schedules || !courtName) return DEFAULT_SCHEDULE;

  // Find matching schedule by checking if court name contains the pattern
  for (const schedule of schedules) {
    if (schedule.pattern && courtName.toLowerCase().includes(schedule.pattern.toLowerCase())) {
      return {
        weekday: schedule.weekday || DEFAULT_SCHEDULE.weekday,
        saturday: schedule.saturday || DEFAULT_SCHEDULE.saturday,
        sunday: schedule.sunday || DEFAULT_SCHEDULE.sunday
      };
    }
  }
  return DEFAULT_SCHEDULE;
};

// Get time slots for a court on a specific date
const getCourtTimes = (courtName, dateString, schedules) => {
  const schedule = getCourtSchedule(courtName, schedules);
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  const dayOfWeek = date.getDay();

  let daySchedule;
  if (dayOfWeek === 0) {
    daySchedule = schedule.sunday;
  } else if (dayOfWeek === 6) {
    daySchedule = schedule.saturday;
  } else {
    daySchedule = schedule.weekday;
  }

  return generateTimeSlots(daySchedule.start, daySchedule.end);
};

const formatDateParts = (dateString, isFrench) => {
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const days = isFrench
    ? ['DIM', 'LUN', 'MAR', 'MER', 'JEU', 'VEN', 'SAM']
    : ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  const months = isFrench
    ? ['JAN', 'FÉV', 'MAR', 'AVR', 'MAI', 'JUN', 'JUL', 'AOÛ', 'SEP', 'OCT', 'NOV', 'DÉC']
    : ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

  const isToday = date.toDateString() === today.toDateString();
  const isTomorrow = date.toDateString() === tomorrow.toDateString();

  return {
    dayName: days[date.getDay()],
    dayNum: String(day),
    month: months[month - 1],
    isToday,
    isTomorrow
  };
};

const translations = {
  en: {
    myCourts: "My Courts",
    noFavorites: "No favorites yet",
    addFirst: "Star your favorite courts from the Search tab to track them here",
    addCourts: "Add Courts",
    searchCourts: "Search courts...",
    available: "available",
    noAvailability: "No availability",
    signIn: "Sign in to track favorites",
    signInDesc: "Save your favorite courts and see all availability at a glance",
    login: "Sign In",
    chooseCourt: "Choose a court",
    noCourtsAt: "No courts at this time",
    back: "Back",
    customize: "Customize",
    availabilitySetup: "Time of day",
    availabilityDesc: "Pick the times you usually play so we can focus your timeline.",
    weekdays: "Weekdays",
    weekends: "Weekends",
    timeOfDay: "Time of day",
    on: "On",
    off: "Off",
    morning: "Morning",
    afternoon: "Afternoon",
    evening: "Evening",
    save: "Save",
    cancel: "Cancel",
    earlier: "Earlier",
    later: "Later",
    useMyLocation: "Use my location",
    locating: "Locating...",
    editOrder: "Edit order",
    done: "Done",
    dragToReorder: "Drag courts to set your custom order",
    todayShort: "Today",
    favoriteLimitTitle: "Favorite limit reached",
    favoriteLimitMessage: "Free accounts can save 1 favorite court. Upgrade to Premium to add more courts to My Courts.",
    upgrade: "Upgrade",
    close: "Close",
    predictiveTitle: "Predictive link (Premium)",
    predictiveNotice: "This time slot is predicted from past booking patterns. The court isn't released for booking yet, so availability isn't guaranteed.",
    openPredictive: "Open predictive link",
    cancel: "Cancel",
    predictiveUpgradeTitle: "Premium-only predictive link",
    predictiveUpgradeBody: "Predictive links open booking pages before the city officially releases them, giving Premium members a head start. Upgrade to unlock these slots."
  },
  fr: {
    myCourts: "Mes Terrains",
    noFavorites: "Aucun favori",
    addFirst: "Ajoutez des favoris depuis l'onglet Recherche pour les suivre ici",
    addCourts: "Ajouter",
    searchCourts: "Rechercher...",
    available: "dispo",
    noAvailability: "Aucune disponibilité",
    signIn: "Connectez-vous",
    signInDesc: "Sauvegardez vos terrains favoris et voyez les disponibilités",
    login: "Connexion",
    chooseCourt: "Choisir un terrain",
    noCourtsAt: "Aucun terrain à cette heure",
    back: "Retour",
    customize: "Personnaliser",
    availabilitySetup: "Moment de la journée",
    availabilityDesc: "Choisissez vos heures pour personnaliser votre timeline.",
    weekdays: "Semaine",
    weekends: "Fin de semaine",
    timeOfDay: "Moments de la journee",
    on: "Oui",
    off: "Non",
    morning: "Matin",
    afternoon: "Après-midi",
    evening: "Soir",
    save: "Enregistrer",
    cancel: "Annuler",
    earlier: "Plus tôt",
    later: "Plus tard",
    useMyLocation: "Utiliser ma position",
    locating: "Localisation...",
    editOrder: "Reorganiser",
    done: "Termine",
    dragToReorder: "Glissez les terrains pour choisir l'ordre",
    todayShort: "Auj.",
    favoriteLimitTitle: "Limite de favoris atteinte",
    favoriteLimitMessage: "Les comptes gratuits peuvent sauvegarder 1 terrain favori. Passez au forfait Premium pour ajouter plus de terrains.",
    upgrade: "Mettre à niveau",
    close: "Fermer",
    predictiveTitle: "Lien prédictif (Premium)",
    predictiveNotice: "Ce créneau est prédit à partir des réservations passées. Le terrain n'est pas encore ouvert à la réservation, la disponibilité n'est pas garantie.",
    openPredictive: "Ouvrir le lien prédictif",
    cancel: "Annuler",
    predictiveUpgradeTitle: "Lien prédictif réservé aux membres Premium",
    predictiveUpgradeBody: "Les liens prédictifs ouvrent les pages de réservation avant la mise en ligne officielle, ce qui donne une longueur d'avance aux membres Premium. Passez à Premium pour débloquer ces créneaux."
  }
};

const PersonalizedCourtsView = ({ isFrench = false, analytics, auth, accountType, updateLastRefreshTime }) => {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [allCourts, setAllCourts] = useState([]);
  const [favorites, setFavoritesState] = useState([]);
  const [quickLinks, setQuickLinks] = useState(null);
  const [predictiveLinks, setPredictiveLinks] = useState(null);
  const [predictiveConfirm, setPredictiveConfirm] = useState(null);
  const [showPredictiveUpgrade, setShowPredictiveUpgrade] = useState(false);
  const [courtSchedules, setCourtSchedules] = useState([]);
  const [selectedDay, setSelectedDay] = useState(0);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showPrefsPanel, setShowPrefsPanel] = useState(false);
  const [periodPrefs, setPeriodPrefs] = useState({ weekday: 1, weekend: 1 });
  const [activePeriodIndex, setActivePeriodIndex] = useState(1);
  const [userLocation, setUserLocation] = useState(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [isEditingOrder, setIsEditingOrder] = useState(false);
  const [draggingCourtId, setDraggingCourtId] = useState(null);
  const [dragMetrics, setDragMetrics] = useState(null);
  const [showFavoriteLimitDialog, setShowFavoriteLimitDialog] = useState(false);
  const favoritesRef = useRef(favorites);
  const dragOrderChangedRef = useRef(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const t = translations[isFrench ? 'fr' : 'en'];
  const canCustomizeOrder = accountType !== 'free' && favorites.length > 1;

  useEffect(() => {
    favoritesRef.current = favorites;
  }, [favorites]);

  useEffect(() => {
    if (!canCustomizeOrder) setIsEditingOrder(false);
  }, [canCustomizeOrder]);

  const handleGetLocation = async () => {
    if (!("geolocation" in navigator)) return;
    setIsLoadingLocation(true);
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          timeout: 10000, enableHighAccuracy: false, maximumAge: 300000
        });
      });
      const location = { latitude: position.coords.latitude, longitude: position.coords.longitude };
      setUserLocation(location);

      // Update courts with distance
      setAllCourts(prev => prev.map(court => {
        if (court.sitePosition) {
          return { ...court, distanceInKm: (getDistance(location, court.sitePosition) / 1000).toFixed(1) };
        }
        return court;
      }).sort((a, b) => (parseFloat(a.distanceInKm) || 999) - (parseFloat(b.distanceInKm) || 999)));
    } catch (e) {
      console.error('Location error:', e);
    }
    setIsLoadingLocation(false);
  };


  // Load data with auto-refresh
  useEffect(() => {
    const fetchData = async (isInitial = false) => {
      if (isInitial) setIsLoading(true);
      const firestore = firebase.firestore();

      try {
        const [overviewSnapshot, quickLinksSnapshot, predictiveLinksSnapshot, schedulesSnapshot] = await Promise.all([
          firestore.collection('overview').doc('loadedSitesTable').get(),
          firestore.collection('overview').doc('quickLinks').get(),
          firestore.collection('overview').doc('predictiveLinks').get(),
          firestore.collection('courtSchedules').get()
        ]);

        const snapShotData = overviewSnapshot.data();
        if (quickLinksSnapshot.exists) {
          setQuickLinks(quickLinksSnapshot.data().quickLinks);
        }
        if (predictiveLinksSnapshot.exists) {
          setPredictiveLinks(predictiveLinksSnapshot.data().predictiveLinks || {});
        } else {
          setPredictiveLinks({});
        }

        // Load court schedules
        const schedules = [];
        schedulesSnapshot.forEach(doc => {
          schedules.push({ id: doc.id, ...doc.data() });
        });
        setCourtSchedules(schedules);

        if (!snapShotData) {
          if (isInitial) setIsLoading(false);
          return;
        }

        // Geolocation fetched in background - don't block loading
        let userLocation = null;

        const updatedData = Object.entries(snapShotData).map(([doc, courtData]) => {
          const court = { ...courtData, id: doc, distanceInKm: null };
          if (userLocation && court.sitePosition) {
            court.distanceInKm = (getDistance(userLocation, court.sitePosition) / 1000).toFixed(1);
          }
          return court;
        });

        updatedData.sort((a, b) => (parseFloat(a.distanceInKm) || 999) - (parseFloat(b.distanceInKm) || 999));
        setAllCourts(updatedData);

        // Update the last refresh time in footer
        if (updateLastRefreshTime) updateLastRefreshTime();
      } catch (error) {
        console.error('Error:', error);
      }
      if (isInitial) setIsLoading(false);
    };

    fetchData(true);

    // Auto-refresh every 10 minutes
    const refreshInterval = setInterval(() => fetchData(false), 600000);
    return () => clearInterval(refreshInterval);
  }, [updateLastRefreshTime]);

  // Load favorites
  useEffect(() => {
    const user = firebase.auth().currentUser;
    if (user) {
      const unsubscribe = firebase.firestore()
        .collection('userFavorites').doc(user.uid)
        .onSnapshot((doc) => {
          if (doc.exists) setFavoritesState(doc.data().favorites || []);
        });
      return () => unsubscribe();
    }
  }, [auth]);

  // Load period preferences from Firestore
  useEffect(() => {
    const user = firebase.auth().currentUser;
    if (user) {
      firebase.firestore().collection('userPreferences').doc(user.uid).get()
        .then((doc) => {
          if (doc.exists && doc.data().myCourtsPrefs) {
            const prefs = doc.data().myCourtsPrefs;
            setPeriodPrefs({ weekday: prefs.weekday ?? 1, weekend: prefs.weekend ?? 1 });
            const today = new Date();
            const isWeekend = today.getDay() === 0 || today.getDay() === 6;
            setActivePeriodIndex(isWeekend ? (prefs.weekend ?? 1) : (prefs.weekday ?? 1));
          }
        })
        .catch((e) => console.error('Error loading prefs:', e));
    }
  }, [auth]);

  const setFavorites = useCallback(async (newFavorites) => {
    const user = firebase.auth().currentUser;
    if (!user) return;
    if (accountType === 'free' && newFavorites.length > 1) {
      setShowFavoriteLimitDialog(true);
      if (analytics) {
        logEvent(analytics, 'favorite_limit_reached', {
          currentCount: favoritesRef.current.length,
          attemptedCount: newFavorites.length,
          source: 'my_courts'
        });
      }
      return;
    }
    try {
      await firebase.firestore().collection('userFavorites').doc(user.uid).set({ favorites: newFavorites });
      setFavoritesState(newFavorites);
      if (analytics) logEvent(analytics, 'favorite_updated', { count: newFavorites.length });
    } catch (e) { console.error(e); }
  }, [accountType, analytics]);

  // Get available dates
  const availableDates = useMemo(() => {
    const dates = new Set();
    allCourts.forEach(court => {
      if (court.tableStats) {
        court.tableStats.forEach(facility => {
          Object.keys(facility.availability || {}).forEach(date => dates.add(date));
        });
      }
    });
    return Array.from(dates).sort().slice(0, 5); // Next 5 days
  }, [allCourts]);

  const userIsPremium = accountType !== 'free';

  // Process favorite courts with availability by time
  const favoriteCourts = useMemo(() => {
    const targetDate = availableDates[selectedDay];
    if (!targetDate) return [];

    const courtsById = new Map(allCourts.map(court => [parseInt(court.siteId), court]));

    return favorites
      .map(favId => courtsById.get(parseInt(favId)))
      .filter(Boolean)
      .map(court => {
        // Get court-specific times based on schedule
        const courtTimes = getCourtTimes(court.siteName, targetDate, courtSchedules);

        // Build availability map for selected date
        const timeSlots = {};
        ALL_TIMES.forEach(t => { timeSlots[t] = []; });

        if (court.tableStats) {
          court.tableStats.forEach(facility => {
            const daySlots = facility.availability?.[targetDate] || [];
            daySlots.forEach(slot => {
              if (!timeSlots[slot.startTime]) return;

              const searchLinkKey = slot.searchLinkKey
                || facility.searchLinks?.[targetDate]?.[slot.startTime]
                || facility.searchLinks?.[targetDate]?.['all'];

              const normalUrl = quickLinks?.[slot.linkKey];
              const predictive = predictiveLinks?.[slot.linkKey];

              let isPredictive = false;
              if (normalUrl) {
                // Normal slot — keep as-is.
              } else if (
                slot.isPredictive
                && predictive
                && isPredictiveSlotVisible(slot)
              ) {
                // Show predictive slots to everyone; free users get an upgrade
                // prompt when they click instead of the actual booking link.
                isPredictive = true;
              } else {
                // No usable link — drop the slot from the visible grid.
                return;
              }

              timeSlots[slot.startTime].push({
                facility: facility.facilityName,
                linkKey: slot.linkKey,
                searchLinkKey,
                isPredictive
              });
            });
          });
        }

        const availableCount = Object.values(timeSlots).filter(s => s.length > 0).length;
        const hasPredictive = Object.values(timeSlots).some(s => s.some(slot => slot.isPredictive));
        return { ...court, timeSlots, availableCount, date: targetDate, courtTimes, hasPredictive };
      });
  }, [allCourts, favorites, availableDates, selectedDay, courtSchedules, quickLinks, predictiveLinks, userIsPremium]);

  const getSlotLink = (slot) => {
    const key = slot.linkKey || slot.searchLinkKey;
    if (!key) return null;
    if (slot.isPredictive) {
      return predictiveLinks?.[key]?.url || null;
    }
    return quickLinks?.[key] || null;
  };

  // Get window times for a specific court (filtered by period on mobile)
  const getWindowTimesForCourt = (courtTimes) => {
    if (isDesktop) {
      return courtTimes;
    }
    const period = PERIODS[activePeriodIndex] || PERIODS[1];
    return courtTimes.filter(time => {
      const hour = parseInt(time.split(':')[0], 10);
      return hour >= period.start && hour < period.end;
    });
  };

  const getAvailabilityColor = (count) => {
    if (count >= 6) return { bg: 'rgba(46, 204, 113, 0.65)', border: 'rgba(46, 204, 113, 0.8)' };
    if (count >= 4) return { bg: 'rgba(76, 175, 80, 0.55)', border: 'rgba(76, 175, 80, 0.7)' };
    if (count >= 2) return { bg: 'rgba(76, 175, 80, 0.4)', border: 'rgba(76, 175, 80, 0.6)' };
    return { bg: 'rgba(76, 175, 80, 0.25)', border: 'rgba(76, 175, 80, 0.5)' };
  };

  const getNowMarkerPercent = (times) => {
    if (!times.length) return null;
    const [startHour] = times[0].split(':').map(Number);
    const [endHour] = times[times.length - 1].split(':').map(Number);
    const startMinutes = startHour * 60;
    const endMinutes = (endHour + 1) * 60;
    const now = new Date();
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    if (nowMinutes < startMinutes || nowMinutes > endMinutes) return null;
    const fraction = (nowMinutes - startMinutes) / (endMinutes - startMinutes);
    return fraction * 100;
  };

  const handleTileClick = (event, court, time) => {
    const slots = court.timeSlots[time] || [];
    if (slots.length === 0) return;

    // Always show selection popover, even for single court
    setSelectedSlot({
      court,
      time,
      slots,
      anchorEl: event.currentTarget
    });
  };

  const closeSelection = () => setSelectedSlot(null);

  const handleCourtSelect = (slot) => {
    if (analytics) logEvent(analytics, 'my_courts_book_court', {
      court: selectedSlot?.court.siteName,
      time: selectedSlot?.time,
      predictive: !!slot.isPredictive
    });
    if (slot.isPredictive && !userIsPremium) {
      if (analytics) logEvent(analytics, 'predictive_upgrade_prompt', {
        court: selectedSlot?.court.siteName,
        time: selectedSlot?.time,
        source: 'my_courts'
      });
      setShowPredictiveUpgrade(true);
      closeSelection();
      return;
    }
    const link = getSlotLink(slot);
    if (!link) {
      closeSelection();
      return;
    }
    if (slot.isPredictive) {
      setPredictiveConfirm({
        url: link,
        facility: slot.facility,
        court: selectedSlot?.court,
        time: selectedSlot?.time
      });
      closeSelection();
      return;
    }
    window.open(link, '_blank');
    closeSelection();
  };

  const handleRemoveFavorite = (e, courtId) => {
    e.stopPropagation();
    setFavorites(favorites.filter(id => id !== parseInt(courtId)));
  };

  const handleAddFavorite = (courtId) => {
    setFavorites([...favorites, parseInt(courtId)]);
    setShowAddDialog(false);
    setSearchQuery('');
  };

  const reorderFavorite = useCallback((dragCourtId, targetCourtId) => {
    const dragId = parseInt(dragCourtId);
    const targetId = parseInt(targetCourtId);
    if (!canCustomizeOrder || dragId === targetId) return;

    const nextFavorites = [...favoritesRef.current];
    const fromIndex = nextFavorites.findIndex(id => parseInt(id) === dragId);
    const toIndex = nextFavorites.findIndex(id => parseInt(id) === targetId);
    if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return;

    const [moved] = nextFavorites.splice(fromIndex, 1);
    nextFavorites.splice(toIndex, 0, moved);
    favoritesRef.current = nextFavorites;
    dragOrderChangedRef.current = true;
    setFavoritesState(nextFavorites);
  }, [canCustomizeOrder]);

  const handleDragStart = (event, courtId) => {
    if (!canCustomizeOrder || !isEditingOrder) return;
    const courtElement = event.currentTarget.closest('[data-court-id]');
    const rect = courtElement?.getBoundingClientRect();
    if (!rect) return;

    const parsedCourtId = parseInt(courtId);
    setDraggingCourtId(parsedCourtId);
    setDragMetrics({
      pointerX: event.clientX,
      pointerY: event.clientY,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      width: rect.width,
      height: rect.height
    });
    dragOrderChangedRef.current = false;
  };

  const handleDragMove = useCallback((event) => {
    if (!draggingCourtId) return;
    event.preventDefault();
    setDragMetrics(prev => prev ? {
      ...prev,
      pointerX: event.clientX,
      pointerY: event.clientY
    } : prev);
    const target = document
      .elementFromPoint(event.clientX, event.clientY)
      ?.closest('[data-court-id]');
    const targetCourtId = target?.getAttribute('data-court-id');
    if (targetCourtId) reorderFavorite(draggingCourtId, targetCourtId);
  }, [draggingCourtId, reorderFavorite]);

  const handleDragEnd = useCallback(() => {
    if (dragOrderChangedRef.current) setFavorites(favoritesRef.current);
    setDraggingCourtId(null);
    setDragMetrics(null);
    dragOrderChangedRef.current = false;
  }, [setFavorites]);

  useEffect(() => {
    if (!draggingCourtId) return undefined;

    const handleWindowPointerMove = (event) => handleDragMove(event);
    const handleWindowPointerEnd = () => handleDragEnd();

    window.addEventListener('pointermove', handleWindowPointerMove, { passive: false });
    window.addEventListener('pointerup', handleWindowPointerEnd);
    window.addEventListener('pointercancel', handleWindowPointerEnd);

    return () => {
      window.removeEventListener('pointermove', handleWindowPointerMove);
      window.removeEventListener('pointerup', handleWindowPointerEnd);
      window.removeEventListener('pointercancel', handleWindowPointerEnd);
    };
  }, [draggingCourtId, handleDragEnd, handleDragMove]);

  // Update period when selected day changes (weekday vs weekend)
  useEffect(() => {
    if (isDesktop) return; // Desktop shows all times
    const targetDate = availableDates[selectedDay];
    if (!targetDate) return;
    const [year, month, day] = targetDate.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
    setActivePeriodIndex(isWeekend ? periodPrefs.weekend : periodPrefs.weekday);
  }, [selectedDay, availableDates, periodPrefs, isDesktop]);

  const searchResults = useMemo(() => {
    const nonFavorites = allCourts.filter(c => !favorites.includes(parseInt(c.siteId)));

    // If search query, filter by name
    if (searchQuery && searchQuery.length >= 2) {
      const q = normalizeCourtSearchValue(searchQuery);
      return nonFavorites
        .filter(c => normalizeCourtSearchValue(c.siteName).includes(q))
        .slice(0, 10);
    }

    // If location available, show top 10 closest
    if (userLocation) {
      return nonFavorites
        .filter(c => c.distanceInKm)
        .slice(0, 10);
    }

    return [];
  }, [allCourts, favorites, searchQuery, userLocation]);

  // Loading
  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress sx={{ borderRadius: 2, height: 3, bgcolor: 'rgba(255,255,255,0.1)' }} />
      </Box>
    );
  }

  // Not logged in
  if (!auth) {
    return (
      <Box sx={{
        p: 4,
        textAlign: 'center',
        maxWidth: 360,
        mx: 'auto',
        mt: 6,
        animation: `${fadeInUp} 0.6s ease-out`
      }}>
        <Box sx={{
          width: 80, height: 80, mx: 'auto', mb: 3,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(255,215,0,0.15) 0%, rgba(255,215,0,0.05) 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <StarBorderIcon sx={{ fontSize: 40, color: '#ffd700' }} />
        </Box>
        <Typography sx={{
          color: '#fff',
          fontSize: '1.5rem',
          fontWeight: 700,
          mb: 1,
          letterSpacing: '-0.02em'
        }}>
          {t.signIn}
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.5)', mb: 4, lineHeight: 1.5 }}>
          {t.signInDesc}
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate('/login')}
          sx={{
            bgcolor: '#ffd700',
            color: '#1a1a2e',
            fontWeight: 700,
            px: 5, py: 1.5,
            borderRadius: '12px',
            textTransform: 'none',
            fontSize: '1rem',
            '&:hover': { bgcolor: '#ffed4a', transform: 'translateY(-2px)' },
            transition: 'all 0.2s ease'
          }}
        >
          {t.login}
        </Button>
      </Box>
    );
  }

  // No favorites
  if (favoriteCourts.length === 0) {
    return (
      <Box sx={{ p: 3, animation: `${fadeInUp} 0.5s ease-out` }}>
        <Typography sx={{
          color: '#fff',
          fontSize: '1.75rem',
          fontWeight: 800,
          letterSpacing: '-0.03em',
          mb: 4
        }}>
          {t.myCourts}
        </Typography>

        <Box sx={{
          textAlign: 'center',
          py: 6,
          px: 3,
          borderRadius: '20px',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.03) 0%, transparent 100%)',
          border: '1px solid rgba(255,255,255,0.06)'
        }}>
          <StarBorderIcon sx={{ fontSize: 56, color: 'rgba(255,215,0,0.3)', mb: 2 }} />
          <Typography sx={{ color: '#fff', fontSize: '1.1rem', fontWeight: 600, mb: 1 }}>
            {t.noFavorites}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.4)', mb: 3, maxWidth: 280, mx: 'auto', lineHeight: 1.5 }}>
            {t.addFirst}
          </Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={() => setShowAddDialog(true)}
            sx={{
              color: '#4caf50',
              border: '2px solid rgba(76,175,80,0.5)',
              borderRadius: '10px',
              px: 3, py: 1,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: 'rgba(76,175,80,0.1)', borderColor: '#4caf50' }
            }}
          >
            {t.addCourts}
          </Button>
        </Box>

        <AddCourtDialog
          open={showAddDialog}
          onClose={() => { setShowAddDialog(false); setSearchQuery(''); }}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          searchResults={searchResults}
          handleAddFavorite={handleAddFavorite}
          t={t}
          isFrench={isFrench}
          onGetLocation={handleGetLocation}
          isLoadingLocation={isLoadingLocation}
          hasLocation={!!userLocation}
        />
      </Box>
    );
  }

  // Main view
  return (
    <Box sx={{
      p: { xs: 1.5, md: 3, lg: 4 },
      pb: 4,
      minHeight: { xs: 'calc(100vh - 16vh)', md: 'calc(100vh - 14vh)' },
      maxWidth: 1100,
      mx: 'auto',
      boxSizing: 'border-box',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: { xs: 2, md: 3 },
        animation: `${fadeInUp} 0.4s ease-out`
      }}>
        <Typography sx={{
          color: '#fff',
          fontSize: '1.75rem',
          fontWeight: 800,
          letterSpacing: '-0.03em'
        }}>
          {t.myCourts}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canCustomizeOrder && (
            <IconButton
              aria-label={isEditingOrder ? t.done : t.editOrder}
              onClick={() => {
                if (isEditingOrder && dragOrderChangedRef.current) {
                  setFavorites(favoritesRef.current);
                  dragOrderChangedRef.current = false;
                }
                setIsEditingOrder(prev => !prev);
                setDraggingCourtId(null);
                setDragMetrics(null);
              }}
              sx={{
                bgcolor: isEditingOrder ? 'rgba(76,175,80,0.25)' : 'rgba(255,255,255,0.08)',
                border: isEditingOrder ? '1px solid rgba(76,175,80,0.6)' : '1px solid transparent',
                '&:hover': { bgcolor: isEditingOrder ? 'rgba(76,175,80,0.35)' : 'rgba(255,255,255,0.15)' }
              }}
            >
              {isEditingOrder ? (
                <CheckIcon sx={{ color: '#81c784' }} />
              ) : (
                <EditIcon sx={{ color: '#fff' }} />
              )}
            </IconButton>
          )}
          {!isDesktop && (
            <IconButton
              onClick={() => setShowPrefsPanel(prev => !prev)}
              sx={{
                bgcolor: 'rgba(255,255,255,0.08)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' }
              }}
            >
              <SettingsIcon sx={{ color: '#fff' }} />
            </IconButton>
          )}
          <IconButton
            onClick={() => setShowAddDialog(true)}
            sx={{
              bgcolor: 'rgba(255,255,255,0.08)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' }
            }}
          >
            <AddIcon sx={{ color: '#fff' }} />
          </IconButton>
        </Box>
      </Box>

      {/* Day selector */}
      <Box sx={{
        display: 'flex',
        justifyContent: { xs: 'flex-start', md: 'center' },
        gap: { xs: 0.75, md: 1.5 },
        mb: { xs: 2, md: 3 },
        animation: `${fadeInUp} 0.5s ease-out`,
        mx: 0,
        px: { xs: 0.5, md: 0 },
        width: '100%',
        overflowX: { xs: 'auto', md: 'visible' },
        overflowY: 'hidden',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
          display: 'none'
        }
      }}>
        {availableDates.map((date, idx) => {
          const dateParts = formatDateParts(date, isFrench);
          const isSelected = selectedDay === idx;
          return (
            <Box
              key={date}
              onClick={() => setSelectedDay(idx)}
              sx={{
                px: { xs: 1.25, md: 2 },
                py: { xs: 0.75, md: 1.5 },
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                bgcolor: isSelected ? '#4caf50' : 'rgba(255,255,255,0.06)',
                border: isSelected ? '1px solid #4caf50' : '1px solid rgba(255,255,255,0.08)',
                '&:hover': {
                  bgcolor: isSelected ? '#4caf50' : 'rgba(255,255,255,0.1)',
                  transform: 'translateY(-2px)'
                },
                textAlign: 'center',
                minWidth: { xs: 48, md: 60 },
                flexShrink: 0,
                position: 'relative',
                overflow: 'hidden',
                ...(dateParts.isToday && {
                  borderColor: isSelected ? '#4caf50' : 'rgba(255, 215, 0, 0.55)',
                  boxShadow: isSelected
                    ? '0 4px 12px rgba(76, 175, 80, 0.3)'
                    : 'inset 0 0 0 1px rgba(255, 215, 0, 0.35)'
                })
              }}
            >
              {dateParts.isToday && (
                <Box sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  bgcolor: isSelected ? 'rgba(255,255,255,0.65)' : '#ffd700',
                  opacity: isSelected ? 0.75 : 0.9
                }} />
              )}
              {dateParts.isToday && isSelected && (
                <Box sx={{
                  position: 'absolute',
                  left: '50%',
                  bottom: -1,
                  transform: 'translateX(-50%)',
                  px: 0.6,
                  py: 0.14,
                  borderRadius: '999px',
                  bgcolor: 'rgba(255,255,255,0.82)',
                  color: '#1f2a33',
                  fontSize: { xs: '0.4rem', md: '0.45rem' },
                  fontWeight: 800,
                  letterSpacing: '0.05em',
                  lineHeight: 1,
                  pointerEvents: 'none'
                }}>
                  {t.todayShort.toUpperCase()}
                </Box>
              )}
              <Typography sx={{
                color: isSelected ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.5)',
                fontWeight: 600,
                fontSize: { xs: '0.6rem', md: '0.65rem' },
                letterSpacing: '0.05em',
                lineHeight: 1.2
              }}>
                {dateParts.dayName}
              </Typography>
              <Typography sx={{
                color: isSelected ? '#fff' : 'rgba(255,255,255,0.85)',
                fontWeight: 700,
                fontSize: { xs: '1.1rem', md: '1.4rem' },
                lineHeight: 1.1
              }}>
                {dateParts.dayNum}
              </Typography>
              <Typography sx={{
                color: isSelected ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.5)',
                fontWeight: 500,
                fontSize: { xs: '0.55rem', md: '0.6rem' },
                letterSpacing: '0.03em',
                lineHeight: 1.2
              }}>
                {dateParts.month}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {showPrefsPanel && !isDesktop && (
        <AvailabilityPrefsPanel
          prefs={periodPrefs}
          onSave={(newPrefs) => {
            setPeriodPrefs(newPrefs);
            // Set active period based on current day
            const today = new Date();
            const isWeekend = today.getDay() === 0 || today.getDay() === 6;
            setActivePeriodIndex(isWeekend ? newPrefs.weekend : newPrefs.weekday);
            setShowPrefsPanel(false);
            // Save to Firestore
            const user = firebase.auth().currentUser;
            if (user) {
              firebase.firestore().collection('userPreferences').doc(user.uid).set({
                myCourtsPrefs: newPrefs
              }, { merge: true }).catch((e) => console.error('Error saving prefs:', e));
            }
          }}
          onClose={() => setShowPrefsPanel(false)}
          t={t}
        />
      )}

      <Box sx={{ pb: 1 }}>
        {isEditingOrder && (
          <Typography sx={{
            color: 'rgba(255,255,255,0.55)',
            fontSize: '0.78rem',
            fontWeight: 600,
            mb: 1.25,
            textAlign: 'center'
          }}>
            {t.dragToReorder}
          </Typography>
        )}
        {/* Period navigation - only on mobile */}
        {!isDesktop && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mb: 1
          }}>
            {activePeriodIndex > 0 ? (
              <Button
                size="small"
                onClick={() => setActivePeriodIndex(prev => prev - 1)}
                startIcon={<ArrowBackIcon sx={{ fontSize: 14 }} />}
                sx={{
                  textTransform: 'none',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '10px',
                  px: 1.5,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' }
                }}
              >
                {t.earlier}
              </Button>
            ) : (
              <Box sx={{ width: 90 }} />
            )}
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', fontWeight: 600 }}>
              {t[PERIODS[activePeriodIndex]?.labelKey || 'afternoon']}
            </Typography>
            {activePeriodIndex < PERIODS.length - 1 ? (
              <Button
                size="small"
                onClick={() => setActivePeriodIndex(prev => prev + 1)}
                endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                sx={{
                  textTransform: 'none',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '10px',
                  px: 1.5,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' }
                }}
              >
                {t.later}
              </Button>
            ) : (
              <Box sx={{ width: 90 }} />
            )}
          </Box>
        )}
        {/* Court rows */}
        {favoriteCourts.map((court, courtIdx) => {
          const courtWindowTimes = getWindowTimesForCourt(court.courtTimes);
          const isDraggingCourt = draggingCourtId === parseInt(court.siteId);
          return (
          <Box
            key={court.siteId}
            data-court-id={court.siteId}
            sx={{
              mb: { xs: 1.5, md: 2.5 },
              animation: isEditingOrder ? 'none' : `${fadeInUp} ${0.4 + courtIdx * 0.08}s ease-out`,
              animationFillMode: 'both',
              minWidth: isDesktop ? 'auto' : courtWindowTimes.length * TIME_SLOT_WIDTH + Math.max(0, courtWindowTimes.length - 1) * TIME_SLOT_GAP,
              minHeight: isDraggingCourt && dragMetrics ? dragMetrics.height : undefined,
              opacity: isDraggingCourt && !dragMetrics ? 0.72 : 1,
              touchAction: isEditingOrder ? 'none' : 'auto',
              transition: isEditingOrder ? 'min-height 0.12s ease, opacity 0.15s ease' : undefined
            }}
          >
            <Box sx={{
              ...(isDraggingCourt && dragMetrics && {
                position: 'fixed',
                left: dragMetrics.pointerX - dragMetrics.offsetX,
                top: dragMetrics.pointerY - dragMetrics.offsetY,
                width: dragMetrics.width,
                zIndex: 1400,
                pointerEvents: 'none',
                transform: 'scale(1.015) rotate(-0.35deg)'
              }),
              p: { xs: 1.5, md: 2.5 },
              borderRadius: '14px',
              bgcolor: 'rgba(255,255,255,0.04)',
              border: isDraggingCourt
                ? '1px solid rgba(129,199,132,0.75)'
                : '1px solid rgba(255,255,255,0.06)',
              boxShadow: isDraggingCourt
                ? '0 18px 42px rgba(0,0,0,0.45), 0 0 0 1px rgba(129,199,132,0.22)'
                : 'none',
              transition: isDraggingCourt ? 'box-shadow 0.18s ease, transform 0.18s ease' : 'all 0.2s ease',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.06)',
                borderColor: 'rgba(255,255,255,0.1)'
              }
            }}>
              {/* Court info */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: { xs: 1, md: 1.5 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    minWidth: 0
                  }}
                >
                  {isEditingOrder && (
                    <IconButton
                      size="small"
                      aria-label={t.editOrder}
                      onPointerDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        event.currentTarget.setPointerCapture?.(event.pointerId);
                        handleDragStart(event, court.siteId);
                      }}
                      sx={{
                        p: 0.25,
                        ml: -0.5,
                        color: 'rgba(255,255,255,0.45)',
                        cursor: isDraggingCourt ? 'grabbing' : 'grab',
                        touchAction: 'none',
                        '&:hover': { color: '#81c784', bgcolor: 'rgba(129,199,132,0.1)' }
                      }}
                    >
                      <DragIndicatorIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  )}
                  <StarIcon sx={{ fontSize: 16, color: '#ffd54f' }} />
                  <Typography sx={{
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    lineHeight: 1.2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {court.siteName.replace('Parc ', '').replace('terrain de sport', '').replace(', terrain de sport', '').replace('Terrains sportifs du parc ', '').trim()}
                  </Typography>
                  {court.siteGoogleMapsLink && (
                    <IconButton
                      size="small"
                      onClick={() => window.open(court.siteGoogleMapsLink, '_blank')}
                      sx={{ p: 0.3, color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#4fc3f7' } }}
                    >
                      <PlaceIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Box>
                {!isEditingOrder && (
                  <IconButton
                    size="small"
                    onClick={(e) => handleRemoveFavorite(e, court.siteId)}
                    sx={{ p: 0.3, color: 'rgba(255,255,255,0.3)', '&:hover': { color: '#ef5350' } }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </Box>

              {/* Time labels per court */}
              <Box sx={{
                display: 'flex',
                gap: `${TIME_SLOT_GAP}px`,
                mb: 0.75,
                justifyContent: isDesktop ? 'flex-start' : (
                  activePeriodIndex === 0 ? 'flex-end' : activePeriodIndex === 2 ? 'flex-start' : 'center'
                )
              }}>
                {courtWindowTimes.map((time) => (
                  <Typography
                    key={time}
                    sx={{
                      width: isDesktop ? 'auto' : TIME_SLOT_WIDTH,
                      flex: isDesktop ? 1 : 'none',
                      textAlign: 'center',
                      fontSize: '0.6rem',
                      color: 'rgba(255,255,255,0.35)',
                      fontWeight: 500,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {time}
                  </Typography>
                ))}
              </Box>

              {/* Time grid */}
              <Box sx={{
                display: 'flex',
                gap: `${TIME_SLOT_GAP}px`,
                position: 'relative',
                justifyContent: isDesktop ? 'flex-start' : (
                  activePeriodIndex === 0 ? 'flex-end' : activePeriodIndex === 2 ? 'flex-start' : 'center'
                )
              }}>
                {(() => {
                // Only show now marker for today (selectedDay === 0)
                if (selectedDay !== 0) return null;
                const markerPercent = getNowMarkerPercent(courtWindowTimes);
                if (markerPercent === null) return null;
                return (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: `${markerPercent}%`,
                        top: -6,
                        bottom: -6,
                        width: 2,
                        borderRadius: 2,
                        bgcolor: 'rgba(255, 215, 0, 0.9)',
                        boxShadow: '0 0 10px rgba(255, 215, 0, 0.8)',
                        pointerEvents: 'none'
                      }}
                    />
                  );
                })()}
                {courtWindowTimes.map(time => {
                  const slots = court.timeSlots[time];
                  const isAvailable = slots && slots.length > 0;
                  const isSelected = selectedSlot && selectedSlot.court.siteId === court.siteId && selectedSlot.time === time;
                  const availabilityColor = isAvailable ? getAvailabilityColor(slots.length) : null;

                  return (
                    <Box
                      key={time}
                      onClick={(event) => {
                        if (!isEditingOrder) handleTileClick(event, court, time);
                      }}
                      sx={{
                        width: isDesktop ? 'auto' : TIME_SLOT_WIDTH,
                        flex: isDesktop ? 1 : 'none',
                        height: { xs: 36, md: 44 },
                        borderRadius: '6px',
                        bgcolor: isAvailable
                          ? availabilityColor.bg
                          : 'transparent',
                        cursor: isAvailable && !isEditingOrder ? 'pointer' : 'default',
                        transition: 'all 0.15s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: isAvailable ? `1px solid ${availabilityColor.border}` : '1px solid rgba(255,255,255,0.08)',
                        position: 'relative',
                        boxSizing: 'border-box',
                        ...(isAvailable && !isEditingOrder && {
                          '&:hover': {
                            bgcolor: availabilityColor.bg,
                            transform: 'scale(1.08)',
                            zIndex: 1,
                            animation: `${pulseGlow} 1.5s infinite`
                          }
                        }),
                        ...(isSelected && {
                          boxShadow: '0 0 0 2px rgba(129, 199, 132, 0.6)',
                          bgcolor: 'rgba(76, 175, 80, 0.55)'
                        }),
                        ...(!isAvailable && {
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            left: 3,
                            right: 3,
                            top: '50%',
                            height: 6,
                            transform: 'translateY(-50%)',
                            borderRadius: 6,
                            background: 'rgba(255,255,255,0.08)'
                          }
                        })
                      }}
                    >
                      {isAvailable && (
                        <Typography sx={{
                          color: '#fff',
                          fontSize: '0.65rem',
                          fontWeight: 700
                        }}>
                          {slots.length}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>

          </Box>
        );})}
      </Box>

      {/* Hint */}
      <AddCourtDialog
        open={showAddDialog}
        onClose={() => { setShowAddDialog(false); setSearchQuery(''); }}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        searchResults={searchResults}
        handleAddFavorite={handleAddFavorite}
        t={t}
        isFrench={isFrench}
        onGetLocation={handleGetLocation}
        isLoadingLocation={isLoadingLocation}
        hasLocation={!!userLocation}
      />
      <Popover
        open={Boolean(selectedSlot)}
        anchorEl={selectedSlot?.anchorEl}
        onClose={closeSelection}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            bgcolor: '#1e2530',
            color: '#fff',
            p: 1.5,
            mt: 0.5,
            minWidth: 180,
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)'
          }
        }}
      >
        {selectedSlot && (() => {
          // Sort slots numerically by extracting number from facility name
          const sortedSlots = [...selectedSlot.slots].sort((a, b) => {
            const numA = parseInt((a.facility || '').match(/\d+/)?.[0] || '0', 10);
            const numB = parseInt((b.facility || '').match(/\d+/)?.[0] || '0', 10);
            return numA - numB;
          });
          const useColumns = sortedSlots.length > 6;
          const rowCount = Math.ceil(sortedSlots.length / 2);

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', mb: 0.25 }}>
                {selectedSlot.time} · {selectedSlot.court.siteName.replace('Parc ', '').replace('terrain de sport', '').replace(', terrain de sport', '').trim()}
              </Typography>
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: useColumns ? '1fr 1fr' : '1fr',
                gridTemplateRows: useColumns ? `repeat(${rowCount}, auto)` : 'auto',
                gridAutoFlow: useColumns ? 'column' : 'row',
                gap: 0.75
              }}>
                {sortedSlots.map((slot, idx) => (
                  <Button
                    key={`${slot.facility}-${idx}`}
                    fullWidth
                    size="small"
                    onClick={() => handleCourtSelect(slot)}
                    sx={{
                      textTransform: 'none',
                      justifyContent: 'flex-start',
                      color: '#fff',
                      bgcolor: slot.isPredictive ? 'rgba(186, 104, 200, 0.18)' : 'rgba(76, 175, 80, 0.2)',
                      border: slot.isPredictive ? '1px dashed rgba(186, 104, 200, 0.6)' : '1px solid rgba(76,175,80,0.4)',
                      borderRadius: '8px',
                      px: 1.5,
                      py: 0.75,
                      fontSize: '0.8rem',
                      fontWeight: 500,
                      '&:hover': { bgcolor: slot.isPredictive ? 'rgba(186, 104, 200, 0.32)' : 'rgba(76, 175, 80, 0.35)' }
                    }}
                  >
                    {slot.isPredictive && <span style={{ marginRight: 6 }}>★</span>}
                    {slot.facility || `${t.chooseCourt} ${idx + 1}`}
                  </Button>
                ))}
              </Box>
            </Box>
          );
        })()}
      </Popover>
      <Dialog
        open={showPredictiveUpgrade}
        onClose={() => setShowPredictiveUpgrade(false)}
        PaperProps={{
          sx: {
            borderRadius: '18px',
            bgcolor: '#1e2530',
            color: '#fff',
            backgroundImage: 'none'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1, color: '#ce93d8' }}>
          ★ {t.predictiveUpgradeTitle}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'rgba(255,255,255,0.75)', lineHeight: 1.55, fontSize: '0.9rem' }}>
            {t.predictiveUpgradeBody}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setShowPredictiveUpgrade(false)}
            sx={{
              color: 'rgba(255,255,255,0.7)',
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            {t.close}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setShowPredictiveUpgrade(false);
              navigate('/profile');
            }}
            sx={{
              bgcolor: '#ab47bc',
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
              '&:hover': { bgcolor: '#ba68c8' }
            }}
          >
            {t.upgrade}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(predictiveConfirm)}
        onClose={() => setPredictiveConfirm(null)}
        PaperProps={{
          sx: {
            borderRadius: '18px',
            bgcolor: '#1e2530',
            color: '#fff',
            backgroundImage: 'none'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1, color: '#ce93d8' }}>
          ★ {t.predictiveTitle}
        </DialogTitle>
        <DialogContent>
          {predictiveConfirm && (
            <Typography sx={{ color: 'rgba(255,255,255,0.75)', lineHeight: 1.55, fontSize: '0.9rem' }}>
              {predictiveConfirm.facility} — {predictiveConfirm.time}
              <Box component="span" sx={{ display: 'block', mt: 1.5 }}>
                {t.predictiveNotice}
              </Box>
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setPredictiveConfirm(null)}
            sx={{
              color: 'rgba(255,255,255,0.7)',
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            {t.cancel}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const url = predictiveConfirm?.url;
              setPredictiveConfirm(null);
              if (url) window.open(url, '_blank');
            }}
            sx={{
              bgcolor: '#ab47bc',
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
              '&:hover': { bgcolor: '#ba68c8' }
            }}
          >
            {t.openPredictive}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showFavoriteLimitDialog}
        onClose={() => setShowFavoriteLimitDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: '18px',
            bgcolor: '#1e2530',
            color: '#fff',
            backgroundImage: 'none'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
          {t.favoriteLimitTitle}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'rgba(255,255,255,0.68)', lineHeight: 1.55 }}>
            {t.favoriteLimitMessage}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setShowFavoriteLimitDialog(false)}
            sx={{
              color: 'rgba(255,255,255,0.7)',
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            {t.close}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setShowFavoriteLimitDialog(false);
              navigate('/profile');
            }}
            sx={{
              bgcolor: '#4caf50',
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
              '&:hover': { bgcolor: '#5fcf65' }
            }}
          >
            {t.upgrade}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Period selector panel for mobile
const AvailabilityPrefsPanel = ({ prefs, onSave, onClose, t }) => {
  const [weekdayPeriod, setWeekdayPeriod] = useState(prefs.weekday || 1);
  const [weekendPeriod, setWeekendPeriod] = useState(prefs.weekend || 1);

  const periods = [
    { key: 'morning', label: t.morning },
    { key: 'afternoon', label: t.afternoon },
    { key: 'evening', label: t.evening }
  ];

  const handleSave = () => {
    onSave({ weekday: weekdayPeriod, weekend: weekendPeriod });
  };

  return (
    <Box sx={{
      mb: 2,
      p: 2,
      borderRadius: '16px',
      bgcolor: 'rgba(255,255,255,0.04)',
      border: '1px solid rgba(255,255,255,0.08)'
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem' }}>
          {t.availabilitySetup}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: 'rgba(255,255,255,0.5)' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Weekday selection */}
      <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem', mb: 1 }}>
        {t.weekdays}
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.75, mb: 2 }}>
        {periods.map((period, idx) => (
          <Button
            key={`weekday-${period.key}`}
            fullWidth
            onClick={() => setWeekdayPeriod(idx)}
            sx={{
              textTransform: 'none',
              py: 1,
              fontSize: '0.8rem',
              color: weekdayPeriod === idx ? '#fff' : 'rgba(255,255,255,0.6)',
              bgcolor: weekdayPeriod === idx ? '#4caf50' : 'rgba(255,255,255,0.06)',
              border: weekdayPeriod === idx ? '1px solid #4caf50' : '1px solid rgba(255,255,255,0.1)',
              borderRadius: '8px',
              fontWeight: weekdayPeriod === idx ? 600 : 500,
              '&:hover': {
                bgcolor: weekdayPeriod === idx ? '#4caf50' : 'rgba(255,255,255,0.1)'
              }
            }}
          >
            {period.label}
          </Button>
        ))}
      </Box>

      {/* Weekend selection */}
      <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem', mb: 1 }}>
        {t.weekends}
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.75, mb: 2 }}>
        {periods.map((period, idx) => (
          <Button
            key={`weekend-${period.key}`}
            fullWidth
            onClick={() => setWeekendPeriod(idx)}
            sx={{
              textTransform: 'none',
              py: 1,
              fontSize: '0.8rem',
              color: weekendPeriod === idx ? '#fff' : 'rgba(255,255,255,0.6)',
              bgcolor: weekendPeriod === idx ? '#4caf50' : 'rgba(255,255,255,0.06)',
              border: weekendPeriod === idx ? '1px solid #4caf50' : '1px solid rgba(255,255,255,0.1)',
              borderRadius: '8px',
              fontWeight: weekendPeriod === idx ? 600 : 500,
              '&:hover': {
                bgcolor: weekendPeriod === idx ? '#4caf50' : 'rgba(255,255,255,0.1)'
              }
            }}
          >
            {period.label}
          </Button>
        ))}
      </Box>

      <Button
        fullWidth
        variant="contained"
        onClick={handleSave}
        sx={{
          textTransform: 'none',
          py: 1,
          bgcolor: '#4caf50',
          borderRadius: '10px',
          fontWeight: 600,
          '&:hover': { bgcolor: '#5fcf65' }
        }}
      >
        {t.save}
      </Button>
    </Box>
  );
};

const AddCourtDialog = ({ open, onClose, searchQuery, setSearchQuery, searchResults, handleAddFavorite, t, isFrench, onGetLocation, isLoadingLocation, hasLocation }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="sm"
    fullWidth
    PaperProps={{
      sx: {
        borderRadius: '20px',
        bgcolor: '#1e2530',
        backgroundImage: 'none',
        maxHeight: '70vh'
      }
    }}
  >
    <DialogTitle sx={{ color: '#fff', fontWeight: 700, pb: 1 }}>
      {t.addCourts}
      <IconButton onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12, color: 'rgba(255,255,255,0.5)' }}>
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent>
      <TextField
        fullWidth
        placeholder={t.searchCourts}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        autoFocus
        sx={{
          mt: 1,
          '& .MuiOutlinedInput-root': {
            bgcolor: 'rgba(255,255,255,0.05)',
            borderRadius: '12px',
            color: '#fff',
            '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
            '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
            '&.Mui-focused fieldset': { borderColor: '#4caf50' }
          }
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'rgba(255,255,255,0.4)' }} />
            </InputAdornment>
          )
        }}
      />
      {!hasLocation && (
        <Button
          fullWidth
          startIcon={<MyLocationIcon />}
          onClick={onGetLocation}
          disabled={isLoadingLocation}
          sx={{
            mt: 1.5,
            py: 1,
            textTransform: 'none',
            color: '#fff',
            bgcolor: 'rgba(255,255,255,0.08)',
            borderRadius: '10px',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
            '&.Mui-disabled': { color: 'rgba(255,255,255,0.5)' }
          }}
        >
          {isLoadingLocation ? t.locating : t.useMyLocation}
        </Button>
      )}
      {searchQuery.length < 2 && hasLocation && searchResults.length > 0 && (
        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', fontWeight: 600, mt: 2, mb: 0.5 }}>
          {isFrench ? "Les plus proches" : "Closest to you"}
        </Typography>
      )}
      <List sx={{ mt: (searchQuery.length < 2 && hasLocation && searchResults.length > 0) ? 0 : 2 }}>
        {searchResults.map(court => (
          <ListItem
            key={court.siteId}
            sx={{
              borderRadius: '12px',
              mb: 0.5,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' }
            }}
          >
            <ListItemText
              primary={court.siteName}
              secondary={court.distanceInKm ? `${court.distanceInKm} km` : null}
              primaryTypographyProps={{ color: '#fff', fontWeight: 500 }}
              secondaryTypographyProps={{ color: 'rgba(255,255,255,0.4)' }}
            />
            <ListItemSecondaryAction>
              <IconButton onClick={() => handleAddFavorite(court.siteId)} sx={{ color: '#4caf50' }}>
                <AddIcon />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
        {searchQuery.length >= 2 && searchResults.length === 0 && (
          <Typography sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.4)', py: 4 }}>
            {isFrench ? "Aucun résultat" : "No results"}
          </Typography>
        )}
        {searchQuery.length < 2 && !hasLocation && (
          <Typography sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.3)', py: 4 }}>
            {isFrench ? "Tapez pour rechercher..." : "Type to search..."}
          </Typography>
        )}
      </List>
    </DialogContent>
  </Dialog>
);

export default PersonalizedCourtsView;
